/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */

import { css, html, LitElement } from 'lit-element';
import { sharedStyles } from '../../styles';
import '../../utils/device-identifier.js'; // just to pull in modrnzr

/**
 * @prop {Boolean} active - Defines if the row is selected or not. Only one row can be active at a time.
 * @prop {Boolean} freeze - If set to `true` , the frozen table style is applied.
 * @prop {String} lwidth - Default 33.3%. Sets the max-width, width, min-width of the frozen-left block in a frozen table.
 * @prop {String} mwidth - Default 33.3%. Sets the max-width, width, min-width of the scrollable block in a frozen table.
 * @prop {String} rwidth - Default 33.3%. Sets the max-width, width, min-width of the frozen-right block in a frozen table.
 * @prop {Boolean} condensed - Adds condensed styles to row.
 * @prop {Boolean} readonly - If set to `true`, adds readonly styles to its children.
 * @fires active-changed - Dispatched when active attribute state has changed.
 * @fires clicked - Dispatched when row is clicked.
 * @fires collapse - Dispatched when row enters collapsed state.
 * @fires expand - Dispatched when row enters expand state.
 * @fires middle-scrolled - Dispatched when content is scrolled.
 * @fires slots-modified - Dispatched so that the table component can adjust the scrollwidth and left position
 * @slot Takes sh-table-head and sh-table-cell as children.
 * @slot frozen-left - This slot is for placing components in the left-most frozen block in a frozen table. Takes sh-table-cell and sh-table-head as children. Refer example of frozen-table for information on usage.
 * @slot scrollable - This slot is for placing components in the scrollable block in a frozen table. Takes sh-table-cell and sh-table-head as children. Refer example of frozen-table for information on usage.
 * @slot frozen-right - This slot is for placing components in the right-most frozen block in a frozen table. Takes sh-table-cell and sh-table-head as children. Refer example of frozen-table for information on usage.
 */
export class SHTableRow extends LitElement {
  static get properties() {
    return {
      lwidth: { type: String, reflect: true },
      rwidth: { type: String, reflect: true },
      mwidth: { type: String, reflect: true },
      /** @ignore */
      scrollLeft: { type: Number },
      /** @ignore */
      active: { type: Boolean, reflect: true },
      freeze: { type: Boolean, reflect: true },
      condensed: { type: Boolean, reflect: true },
      /** @ignore */
      chapter: { type: Boolean, reflect: true },
      /** @ignore */
      expanded: { type: Boolean, reflect: true },
      readonly: { type: Boolean, reflect: true },
      _hasChapter: { type: Boolean, reflect: false },
    };
  }

  static get styles() {
    return [
      sharedStyles,
      css`
        :host {
          outline: 0;
          display: block;
        }

        :host > div {
          border-bottom: 0.0625rem solid rgba(var(--ui-1), var(--opacity-6));
          display: flex;
          flex-direction: row;
          transition: var(--transition-time) all ease-in-out, 0.1s background ease;
          min-height: fit-content;
          min-height: -ms-fit-content;
          min-height: -moz-fit-content;
        }

        :host([slot='header']) > div {
          border-bottom: 0.0625rem solid rgba(var(--ui-1), var(--opacity-4));
        }

        /* column assignment */

        #wrapper > ::slotted([columns='24']),
        #wrapper > div > ::slotted([columns='24']) {
          width: 100%;
        }

        #wrapper > ::slotted([columns='23']),
        #wrapper > div > ::slotted([columns='23']) {
          width: calc((100% / 24) * 23);
        }

        #wrapper > ::slotted([columns='22']),
        #wrapper > div > ::slotted([columns='22']) {
          width: calc((100% / 24) * 22);
        }

        #wrapper > ::slotted([columns='21']),
        #wrapper > div > ::slotted([columns='21']) {
          width: calc((100% / 24) * 21);
        }

        #wrapper > ::slotted([columns='20']),
        #wrapper > div > ::slotted([columns='20']) {
          width: calc((100% / 24) * 20);
        }

        #wrapper > ::slotted([columns='19']),
        #wrapper > div > ::slotted([columns='19']) {
          width: calc((100% / 24) * 19);
        }

        #wrapper > ::slotted([columns='18']),
        #wrapper > div > ::slotted([columns='18']) {
          width: calc((100% / 24) * 18);
        }

        #wrapper > ::slotted([columns='17']),
        #wrapper > div > ::slotted([columns='17']) {
          width: calc((100% / 24) * 17);
        }

        #wrapper > ::slotted([columns='16']),
        #wrapper > div > ::slotted([columns='16']) {
          width: calc((100% / 24) * 16);
        }

        #wrapper > ::slotted([columns='15']),
        #wrapper > div > ::slotted([columns='15']) {
          width: calc((100% / 24) * 15);
        }

        #wrapper > ::slotted([columns='14']),
        #wrapper > div > ::slotted([columns='14']) {
          width: calc((100% / 24) * 14);
        }

        #wrapper > ::slotted([columns='13']),
        #wrapper > div > ::slotted([columns='13']) {
          width: calc((100% / 24) * 13);
        }

        #wrapper > ::slotted([columns='12']),
        #wrapper > div > ::slotted([columns='12']) {
          width: calc((100% / 24) * 12);
        }

        #wrapper > ::slotted([columns='11']),
        #wrapper > div > ::slotted([columns='11']) {
          width: calc((100% / 24) * 11);
        }

        #wrapper > ::slotted([columns='10']),
        #wrapper > div > ::slotted([columns='10']) {
          width: calc((100% / 24) * 10);
        }

        #wrapper > ::slotted([columns='9']),
        #wrapper > div > ::slotted([columns='9']) {
          width: calc((100% / 24) * 9);
        }

        #wrapper > ::slotted([columns='8']),
        #wrapper > div > ::slotted([columns='8']) {
          width: calc((100% / 24) * 8);
        }

        #wrapper > ::slotted([columns='7']),
        #wrapper > div > ::slotted([columns='7']) {
          width: calc((100% / 24) * 7);
        }

        #wrapper > ::slotted([columns='6']),
        #wrapper > div > ::slotted([columns='6']) {
          width: calc((100% / 24) * 6);
        }

        #wrapper > ::slotted([columns='5']),
        #wrapper > div > ::slotted([columns='5']) {
          width: calc((100% / 24) * 5);
        }

        #wrapper > ::slotted([columns='4']),
        #wrapper > div > ::slotted([columns='4']) {
          width: calc((100% / 24) * 4);
        }

        #wrapper > ::slotted([columns='3']),
        #wrapper > div > ::slotted([columns='3']) {
          width: calc((100% / 24) * 3);
        }

        #wrapper > ::slotted([columns='2']),
        #wrapper > div > ::slotted([columns='2']) {
          width: calc((100% / 24) * 2);
        }

        #wrapper > ::slotted([columns='1']),
        #wrapper > div > ::slotted([columns='1']) {
          width: calc((100% / 24) * 1);
        }

        #wrapper > ::slotted([columns='0']),
        #wrapper > div > ::slotted([columns='0']) {
          display: none;
        }

        /* column assignment for medium breakpoint */

        @media only screen and (max-width: 64.0625rem) {
          #wrapper > ::slotted([col-m='24']) {
            width: 100%;
          }
          #wrapper > ::slotted([col-m='23']) {
            width: calc((100% / 24) * 23);
          }
          #wrapper > ::slotted([col-m='22']) {
            width: calc((100% / 24) * 22);
          }
          #wrapper > ::slotted([col-m='21']) {
            width: calc((100% / 24) * 21);
          }
          #wrapper > ::slotted([col-m='20']) {
            width: calc((100% / 24) * 20);
          }
          #wrapper > ::slotted([col-m='19']) {
            width: calc((100% / 24) * 19);
          }
          #wrapper > ::slotted([col-m='18']) {
            width: calc((100% / 24) * 18);
          }
          #wrapper > ::slotted([col-m='17']) {
            width: calc((100% / 24) * 17);
          }
          #wrapper > ::slotted([col-m='16']) {
            width: calc((100% / 24) * 16);
          }
          #wrapper > ::slotted([col-m='15']) {
            width: calc((100% / 24) * 15);
          }
          #wrapper > ::slotted([col-m='14']) {
            width: calc((100% / 24) * 14);
          }
          #wrapper > ::slotted([col-m='13']) {
            width: calc((100% / 24) * 13);
          }
          #wrapper > ::slotted([col-m='12']) {
            width: calc((100% / 24) * 12);
          }
          #wrapper > ::slotted([col-m='11']) {
            width: calc((100% / 24) * 11);
          }
          #wrapper > ::slotted([col-m='10']) {
            width: calc((100% / 24) * 10);
          }
          #wrapper > ::slotted([col-m='9']) {
            width: calc((100% / 24) * 9);
          }
          #wrapper > ::slotted([col-m='8']) {
            width: calc((100% / 24) * 8);
          }
          #wrapper > ::slotted([col-m='7']) {
            width: calc((100% / 24) * 7);
          }
          #wrapper > ::slotted([col-m='6']) {
            width: calc((100% / 24) * 6);
          }
          #wrapper > ::slotted([col-m='5']) {
            width: calc((100% / 24) * 5);
          }
          #wrapper > ::slotted([col-m='4']) {
            width: calc((100% / 24) * 4);
          }
          #wrapper > ::slotted([col-m='3']) {
            width: calc((100% / 24) * 3);
          }
          #wrapper > ::slotted([col-m='2']) {
            width: calc((100% / 24) * 2);
          }
          #wrapper > ::slotted([col-m='1']) {
            width: calc((100% / 24) * 1);
          }
          #wrapper > ::slotted([col-m='0']) {
            display: none;
          }
        }

        /* column assignment for small breakpoint */

        @media only screen and (max-width: 47.9375rem) {
          #wrapper > ::slotted([col-s='24']) {
            width: 100%;
          }
          #wrapper > ::slotted([col-s='23']) {
            width: calc((100% / 24) * 23);
          }
          #wrapper > ::slotted([col-s='22']) {
            width: calc((100% / 24) * 22);
          }
          #wrapper > ::slotted([col-s='21']) {
            width: calc((100% / 24) * 21);
          }
          #wrapper > ::slotted([col-s='20']) {
            width: calc((100% / 24) * 20);
          }
          #wrapper > ::slotted([col-s='19']) {
            width: calc((100% / 24) * 19);
          }
          #wrapper > ::slotted([col-s='18']) {
            width: calc((100% / 24) * 18);
          }
          #wrapper > ::slotted([col-s='17']) {
            width: calc((100% / 24) * 17);
          }
          #wrapper > ::slotted([col-s='16']) {
            width: calc((100% / 24) * 16);
          }
          #wrapper > ::slotted([col-s='15']) {
            width: calc((100% / 24) * 15);
          }
          #wrapper > ::slotted([col-s='14']) {
            width: calc((100% / 24) * 14);
          }
          #wrapper > ::slotted([col-s='13']) {
            width: calc((100% / 24) * 13);
          }
          #wrapper > ::slotted([col-s='12']) {
            width: calc((100% / 24) * 12);
          }
          #wrapper > ::slotted([col-s='11']) {
            width: calc((100% / 24) * 11);
          }
          #wrapper > ::slotted([col-s='10']) {
            width: calc((100% / 24) * 10);
          }
          #wrapper > ::slotted([col-s='9']) {
            width: calc((100% / 24) * 9);
          }
          #wrapper > ::slotted([col-s='8']) {
            width: calc((100% / 24) * 8);
          }
          #wrapper > ::slotted([col-s='7']) {
            width: calc((100% / 24) * 7);
          }
          #wrapper > ::slotted([col-s='6']) {
            width: calc((100% / 24) * 6);
          }
          #wrapper > ::slotted([col-s='5']) {
            width: calc((100% / 24) * 5);
          }
          #wrapper > ::slotted([col-s='4']) {
            width: calc((100% / 24) * 4);
          }
          #wrapper > ::slotted([col-s='3']) {
            width: calc((100% / 24) * 3);
          }
          #wrapper > ::slotted([col-s='2']) {
            width: calc((100% / 24) * 2);
          }
          #wrapper > ::slotted([col-s='1']) {
            width: calc((100% / 24) * 1);
          }
          #wrapper > ::slotted([col-s='0']) {
            display: none;
          }
        }

        /* chapter menu */

        .chapter-wrapper {
          transition: 0.1s all ease-in-out;
        }

        :host(:not([expanded])) > .chapter-wrapper {
          max-height: 0rem;
          overflow: hidden;
          pointer-events: none;
          transform: translateY(-0.5rem);
          opacity: 0;
          display: none;
        }

        :host([chapter]) > .chapter-wrapper {
          flex-direction: column;
          border: none;
        }

        :host(:not([chapter])) > #wrapper > .arrow-icon {
          display: none;
        }

        :host([chapter]) > #wrapper > .arrow-icon {
          display: block;
          margin-top: 0.5rem;
        }

        :host([chapter][expanded]) > #wrapper > .arrow-icon {
          transform: rotate(90deg);
        }

        :host(:not([chapter]).chapter-sibling) > #wrapper > .menu-item-wrapper {
          margin-left: 2rem;
        }

        /* hover */
        :host(:not(.no-hovermq):not([slot='header']):not([readonly])) > #wrapper:hover {
          background: rgba(var(--ui-1), var(--opacity-7));
        }
        :host([slot='header']:not(.no-hovermq)) > div:hover {
          background: transparent;
        }
        :host(:not([readonly])[active]) #wrapper {
          background: rgba(var(--ui-1), var(--opacity-6)) !important;
        }
        :host(:not([freeze])) .frozen-left,
        :host(:not([freeze])) .scrollable,
        :host(:not([freeze])) .frozen-right {
          display: none !important;
        }
        .frozen-left,
        .frozen-right,
        .scrollable {
          display: flex;
          align-items: center;
        }
        .frozen-left {
          box-shadow: inset -0.125rem 0rem 0rem 0rem rgba(var(--ui-1), var(--opacity-6));
        }
        .frozen-right {
          box-shadow: inset 0.125rem 0rem 0rem 0rem rgba(var(--ui-1), var(--opacity-6));
        }
        .scrollable {
          overflow-x: hidden;
          overflow-y: hidden;
        }
        /* menu Expand Icon */
        :host #wrapper {
          position: relative;
        }
        :host #wrapper #tableExpandIcon {
          position: absolute;
          left: 0.75rem;
        }
        :host([condensed]) #tableExpandIcon {
          margin-top: 0.25rem !important;
        }
      `,
    ];
  }

  render() {
    return html`
      <div
        id="wrapper"
        class="${this.active ? 'active' : ''}"
        @click="${(e) => this.handleClick(e)}"
      >
        ${this.chapter
          ? html`
              <sh-icon
                id="tableExpandIcon"
                button
                icon="arrow-right-s"
                size="m"
                class="arrow-icon"
                @click="${() => {
                  this.handleExpanded();
                }}"
              ></sh-icon>
            `
          : ''}
        <slot id="mainBody" @slotchange="${() => this.mainBodyObserver()}"></slot>
        ${this.freeze
          ? html` <div
                class="frozen-left"
                style="display: flex; max-width: ${this.lwidth}; width: ${this
                  .lwidth}; min-width: ${this.lwidth}"
              >
                <slot name="frozen-left" id="frozenLeftSlot"></slot>
              </div>
              <div
                class="scrollable"
                style="display: flex; max-width: ${this.mwidth}; width: ${this
                  .mwidth}; min-width:${this.mwidth}"
                @scroll="${(e) => this.dispatchCustomScrollEvent(e)}"
              >
                <slot name="scrollable" id="scrollableSlot"></slot>
              </div>
              <div
                class="frozen-right"
                style="display: flex; max-width: ${this.rwidth};  width: ${this
                  .rwidth}; min-width: ${this.rwidth}"
              >
                <slot name="frozen-right" id="frozenRightSlot"></slot>
              </div>`
          : ''}
      </div>
      ${this.chapter
        ? html` <div class="chapter-wrapper">
            <slot name="chapterSlot"></slot>
          </div>`
        : ''}
    `;
  }

  attributeChangedCallback(name, oldval, newval) {
    super.attributeChangedCallback(name, oldval, newval);
    if (name === 'condensed') {
      this.condensedObserver();
    } else if (name === 'readonly') {
      this.readonlyObserver();
    }
  }

  updated(changedProperties) {
    if (changedProperties.has('active')) {
      this.dispatchEvent(new Event(`active-changed`));
      const table = this.closest('sh-table');
      /*update selectedRows of the sh-table on active change*/
      if (this.active) {
        table.selectedRows.push(this);
      } else {
        /*when row is deselected, remove it from the selectedRows array*/
        if (table.selectedRows.includes(this)) {
          table.selectedRows.splice(table.selectedRows.indexOf(this), 1);
        }
      }
    }
    if (changedProperties.has('scrollLeft')) {
      this.scrollLeftChanged();
    }
    if (changedProperties.has('chapter')) {
      this.setExpandIconPosition();
    }
  }

  condensedObserver() {
    const children = this.children;
    for (let i = 0; i < children.length; i++) {
      children[i].condensed = this.condensed;
    }
  }

  readonlyObserver() {
    const numberOfChildren = this.children.length;
    for (let i = 0; i < numberOfChildren; i++) {
      if (this.children[i].tagName === 'SH-TABLE-ROW') {
        this.children[i].readonly = this.readonly;
      }
    }
  }

  scrollLeftChanged() {
    this.shadowRoot.querySelector('.scrollable').scrollLeft = this.scrollLeft;
  }

  constructor() {
    super();
    this.lwidth = '33.3%';
    this.rwidth = '33.3%';
    this.mwidth = '33.3%';
  }

  connectedCallback() {
    super.connectedCallback();
    const children = this.children;
    let counter = 0;
    let index;
    if (this.slot === 'header') {
      for (let i = 0; i < children.length; i++) {
        if (children[i].sorted) {
          counter++;
          index = i;
        }
      }
      if (counter > 1) {
        let suffix;
        switch (index + 1) {
          case 2:
            suffix = 'nd';
            break;
          case 3:
            suffix = 'rd';
            break;
          default:
            suffix = 'th';
            break;
        }
        console.warn(
          `%c\t${counter} table-head have sorted property.\n`,
          'color:white;font-weight:900;text-align:center'
        );
        console.warn(
          `%c\tOnly one table-head with the sorted property is allowed.\n`,
          'color:white;font-weight:900;text-align:center'
        );
        console.warn(
          `%c\t${index + 1}${suffix} table-head with the sorted property is given the arrow.\n`,
          'color:white;font-weight:900;text-align:center'
        );
        this.removeDuplicateSortedProperty(index);
      }
      this.addEventListener('showarrow', function (event) {
        for (let i = 0; i < children.length; i++) {
          if (children[i] === event.target) {
            index = i;
            break;
          }
        }
        this.removeDuplicateSortedProperty(index);
      });
    }
  }

  removeDuplicateSortedProperty(index) {
    for (let i = 0; i < this.children.length; i++) {
      if (index !== i) {
        this.children[i].sorted = false;
      }
    }
  }

  firstUpdated() {
    if (!Modernizr.hovermq) {
      this.classList.add('no-hovermq');
    }
    if (this.freeze) {
      this.shadowRoot
        .querySelector('#scrollableSlot')
        .addEventListener('slotchange', () => this.dispatchSlotModifiedEventWithData());
      this.shadowRoot
        .querySelector('#frozenLeftSlot')
        .addEventListener('slotchange', () => this.dispatchSlotModifiedEventWithData());
      this.shadowRoot
        .querySelector('#frozenRightSlot')
        .addEventListener('slotchange', () => this.dispatchSlotModifiedEventWithData());
    }
  }

  handleClick(event) {
    const rowType = this.getAttribute('slot');
    if (rowType !== 'header' && event.target.id !== 'tableExpandIcon' && !this.readonly) {
      this.dispatchEvent(
        new CustomEvent('clicked', {
          bubbles: true,
          composed: true,
        })
      );
      /*Allows multiselection with ctrl and shft key press*/
      if (this.closest('sh-table').selectionMode === 'metakey') {
        this._onMultiSelectKeyPress(event);
      } else if (!this.closest('sh-table').multiselect) {
        /*If not multiselect , single row selection should happen on click*/
        this.active = true;
      }
    }
  }

  /*Handles Multiselection for ctrl key and shift key press*/
  _onMultiSelectKeyPress(event) {
    let table = this.closest('sh-table');
    let allTableRows = table.querySelectorAll('sh-table-row');

    if (event.ctrlKey || event.metaKey) {
      this.active = !this.active;
    } else if (event.shiftKey) {
      this._onShiftKeyPress(allTableRows);
    } else if (!event.ctrlKey && !event.metaKey && !event.shiftKey) {
      for (let i = 0; i < allTableRows.length; i++) {
        allTableRows[i].removeAttribute('active');
      }
      this.active = true;
      this.closest('sh-table').selectedRows = [];
    }
  }

  /*Handles selection of range of rows for row click + shift key press*/
  _onShiftKeyPress(tableRows) {
    let prevRowIndex, currentRowIndex;
    const selectedRows = this.closest('sh-table').selectedRows;
    const previousActiveRow = selectedRows[selectedRows.length - 1];
    let rowsArray = Array.prototype.slice.call(tableRows);

    /*Index of previously clicked row*/
    if (rowsArray.includes(previousActiveRow)) {
      prevRowIndex = rowsArray.indexOf(previousActiveRow);
    }
    /*Index of currently clicked row*/
    if (rowsArray.includes(this)) {
      currentRowIndex = rowsArray.indexOf(this);
    }
    /*select or deselect row based on it's previous state(active/inactive)*/
    if (prevRowIndex >= 0 && currentRowIndex >= 0) {
      if (!tableRows[currentRowIndex].active) {
        this._selectDeselectRows(tableRows, prevRowIndex, currentRowIndex, true);
      } else {
        this._selectDeselectRows(tableRows, prevRowIndex, currentRowIndex, false);
      }
    } else if ((prevRowIndex && !currentRowIndex) || (currentRowIndex && !prevRowIndex)) {
      /*if either previous active or current active is undefined , single row selection should happen based on the active index*/
      const activeIndex = currentRowIndex ? currentRowIndex : prevRowIndex;
      tableRows[activeIndex].active = true;
    }
  }

  /*Handles selection / deselection of range of rows based on it's active value*/
  _selectDeselectRows(tableRows, prev, curr, activeValue) {
    const startIndex = prev;
    /*select range of rows (currently clicked row - previously active row)*/
    const range = Math.abs(curr - prev);
    for (let k = 0; k <= range; k++) {
      /*select range of rows Ex: (1 to 4)*/
      if (prev < curr) {
        tableRows[prev].active = activeValue;
        prev++;
      } else if (prev > curr) {
        /*select range of rows Ex: (4 to 1)*/
        tableRows[prev].active = activeValue;
        prev--;
      }
    }
    /*current clicked row is active for both selection / deselection*/
    if (activeValue) {
      tableRows[curr].active = activeValue;
    } else if (startIndex === curr) {
      /*if currently clicked row is same as previously clicked -> select/deselect based on it's previous state*/
      tableRows[curr].active = !tableRows[curr].active;
    }
  }

  dispatchCustomScrollEvent(e) {
    const scrollLeft = e.target.scrollLeft;
    this.dispatchEvent(
      new CustomEvent('middle-scrolled', {
        bubbles: true,
        composed: true,
        detail: { event: e, scrollLeft },
      })
    );
  }

  handleExpanded() {
    if (this.expanded === true) {
      this.expanded = false;
      this.dispatchEvent(
        new CustomEvent('collapse', {
          detail: this,
          composed: true,
          bubbles: true,
        })
      );
    } else {
      this.expanded = true;
      this.dispatchEvent(
        new CustomEvent('expand', {
          detail: this,
          composed: true,
          bubbles: true,
        })
      );
    }
  }

  mainBodyObserver() {
    const tableRows = this.parentNode.children;
    if (tableRows.length > 0) {
      for (let i = 0; i < tableRows.length; i++) {
        let rowChildLength = Array.from(tableRows[i].children).filter(
          (x) => x.tagName == 'SH-TABLE-ROW'
        ).length;
        if (rowChildLength > 0) {
          this._hasChapter = true;
          break;
        }
      }
    }
    this.nestRows();
    const numberOfChildren = this.children.length;
    for (let i = 0; i < numberOfChildren; i++) {
      if (this.children[i].tagName === 'SH-TABLE-ROW') {
        this.children[i].readonly = this.readonly;
      }
    }
  }

  dispatchSlotModifiedEventWithData() {
    /**
     * Event named 'slots-modifed' is dispatched so that the table component can adjust the scrollwidth and left position
     * of floating scrollbar of the parent table component.
     *
     * The data that is sent along with this event are:
     *
     * 1. innerWidth    -  the scrollwidth of the scrollable div - sending to adjust width of floating scrollbar thumb.
     * 2. width         -  the computed width of the scrollable div - sending to adjust width of floating scrollbar.
     * 3. leftPosition  -  the computed width of the frozen-left div - sending to adjust left position of floating scrollbar.
     *
     *
     */
    if (
      this.shadowRoot.querySelector('.scrollable') ||
      this.shadowRoot.querySelector('.frozen-left')
    ) {
      const innerWidth = this.shadowRoot.querySelector('.scrollable').scrollWidth;
      const width = this.shadowRoot.querySelector('.scrollable').getBoundingClientRect().width;
      const leftPosition = this.shadowRoot
        .querySelector('.frozen-left')
        .getBoundingClientRect().width;
      this.dispatchEvent(
        new CustomEvent('slots-modified', {
          bubbles: true,
          composed: true,
          detail: { innerWidth, width, leftPosition },
        })
      );
    }
  }

  getParents(elem) {
    const parents = [];
    for (; elem && elem !== document && elem.tagName === 'SH-TABLE-ROW'; elem = elem.parentNode) {
      parents.push(elem);
    }
    return parents;
  }

  nestRows() {
    //check if row is in the header slot
    if (this.slot !== 'header') {
      // Get all table rows
      const childRows = Array.prototype.slice.call(this.childNodes).filter(function (element) {
        if (element.tagName === 'SH-TABLE-ROW') {
          return element;
        }
      });
      // if it has table rows within it, then this is a chapter
      // set the slot of each child rows to chapterSlot
      if (childRows.length > 0) {
        this.chapter = true;
        childRows.forEach((element) => {
          element.setAttribute('slot', 'chapterSlot');
        });
      }
    }
    // if this is is a chapter,
    // add class chapter-sibling to each sibling element.
    if (this.chapter || this._hasChapter) {
      const siblingItems = this.parentNode.children;
      if (this.parentNode.tagName !== 'SH-TABLE-ROW') {
        for (let i = 0; i < siblingItems.length; i++) {
          siblingItems[i].classList.add('chapter-sibling');
        }
      }
    }
    var mainBodySlot = this.shadowRoot.querySelector('#mainBody');
    var slotFirstChild = mainBodySlot.assignedElements()[0];
    let isCheckbox;

    if (slotFirstChild) {
      isCheckbox = slotFirstChild.hasAttribute('checkbox');
    }
    // if this is a chapter or this has class as chapter-sibling or this has been assigned to slot chapterSlot
    if (
      this.chapter ||
      this.classList.contains('chapter-sibling') ||
      this.slot === 'chapterSlot' ||
      isCheckbox
    ) {
      //add padding by level of nesting
      this._setCellPosition(isCheckbox);
    }
    if (
      !(this.chapter || this.classList.contains('chapter-sibling') || this.slot === 'chapterSlot')
    ) {
      const firstCell = this.querySelector('sh-table-cell:not([checkbox])');
      const firstHeader =
        this.slot === 'header' ? this.querySelector('sh-table-head:not([checkbox])') : '';
      let tableFirstCell = firstHeader ? firstHeader : firstCell;
      if (tableFirstCell) {
        tableFirstCell.style.paddingLeft = isCheckbox ? `calc(${'2.5rem'})` : `calc(${'0rem'})`;
      }
    }
  }

  _setCellPosition(isCheckbox) {
    const parentRowAmount = this.getParents(this).length;
    const cellPaddingLeft = isCheckbox ? '4.5rem' : '3.5rem';
    const nestedCellPaddingLeft = '2.75rem';
    const firstCell = this.querySelector('sh-table-cell:not([checkbox])');
    const firstheader =
      this.slot === 'header' ? this.querySelector('sh-table-head:not([checkbox])') : '';

    if (firstCell) {
      if (parentRowAmount === 1) {
        firstCell.style.paddingLeft = cellPaddingLeft;
      } else {
        firstCell.style.paddingLeft = `calc(${cellPaddingLeft} + ${
          parentRowAmount - 1
        } * ${nestedCellPaddingLeft})`;
      }
      const checkBoxRow = this.querySelector('sh-table-cell[checkbox]');
      checkBoxRow ? this._setCheckboxPostion(isCheckbox, parentRowAmount, checkBoxRow) : '';
    }
    if (firstheader) {
      const checkboxHeader = this.querySelector('sh-table-head[checkbox]');
      checkboxHeader ? this._setCheckboxPostion(isCheckbox, parentRowAmount, checkboxHeader) : '';
      firstheader.style.paddingLeft = isCheckbox
        ? `calc(${cellPaddingLeft} + ${parentRowAmount - 1} * ${nestedCellPaddingLeft})`
        : `calc(${'0rem'})`;
    }
  }

  _setCheckboxPostion(isCheckbox, parentRowAmount, checkBox) {
    if (isCheckbox) {
      var val = (parentRowAmount - 1) * 2.5;
      checkBox.style.position = 'absolute';
      checkBox.style.left = `calc(${val + 'rem'})`;
    } else {
      if (checkBox) {
        checkBox.style.position = 'unset';
        checkBox.style.left = `0rem`;
      }
    }
  }

  setExpandIconPosition() {
    if (!this.chapter) return;
    const expandIcon = this.shadowRoot.querySelector('#tableExpandIcon');
    const parentNestingLevel = this.getParents(this).length;
    const marginLeft = '3.5rem';
    const nestedMarginLeft = '2.75rem';

    let checkbox = this.querySelector('sh-table-cell[checkbox]');
    let mainBodySlot = this.shadowRoot.querySelector('#mainBody');
    let firstColumnCheckBox;
    if (mainBodySlot.assignedElements()[0]) {
      firstColumnCheckBox = mainBodySlot.assignedElements()[0].hasAttribute('checkbox');
    }
    let iconLeftValue;
    if (checkbox && firstColumnCheckBox) {
      let checkboxLeft = checkbox.style.left.split('(')[1].split(')')[0].split('rem')[0];
      iconLeftValue = Number(checkboxLeft) + 3;
    }
    expandIcon.style.left = firstColumnCheckBox
      ? `calc(${iconLeftValue + 'rem'})`
      : `calc(${marginLeft} + ${parentNestingLevel - 2} * ${nestedMarginLeft})`;
  }
}

customElements.define('sh-table-row', SHTableRow);
